import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    MenuItem,
    Alert,
    OutlinedInput,
    InputAdornment,
    Paper,
    List,
    ListItem,
    ListItemButton
} from '@mui/material';

import { IconPassword, IconSearch } from '@tabler/icons';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { useHistory } from 'react-router-dom';
import { token } from '../../../../../api';
import api from '../../../../services/api';
import CustomFieldInput from '../shared/CustomFieldInput.js';
import { isFieldVisible } from '../shared/fieldVisibility';

const CreateUser = () => {
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        username: '',
        role: '',
        phone: '',
        avatar: ''
    });

    const [status, setStatus] = useState({ type: '', message: '' });
    const [appId, setAppId] = useState(null);

    // --- Busca de Organização ---
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);

    // --- Campos customizados ---
    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [fields, setFields] = useState({});

    const history = useHistory();

    useEffect(() => {
        load_app();
    }, []);

    useEffect(() => {
        if (!appId) return;
        // loadChatbots();
        loadFields();
    }, [appId]);

    const load_app = async () => {
        console.log(`entrou em load_app`);
        const resp = await api.get(`/v1/apps/default/`);
        if (resp.data) {
            console.log(`load_app - resp.data: ${JSON.stringify(resp.data)}`);
            setAppId(resp.data.app.id);
        }
    };

    const loadFields = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/client_fields/`);
            setFieldsConfig(response.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    // Atualiza o formulário
    const handleChange = (e) => {
        const { name, value } = e.target;

        const newData = {
            ...formData,
            [name]: value
        };

        // Gerar username automaticamente
        if (name === 'first_name' || name === 'last_name') {
            const fn = name === 'first_name' ? value : formData.first_name;
            const ln = name === 'last_name' ? value : formData.last_name;

            if (fn && ln) {
                newData.username = `${fn.toLowerCase()}.${ln.toLowerCase()}`;
            }
        }

        setFormData(newData);
    };

    const backPage = () => {
        // setShowResults(false);
        history.push(`/dashboard/config/users/view`);
    };

    // Busca organizacoes c/ debounce
    const handleSearch = useCallback(
        debounce(async (value) => {
            if (!value.trim()) {
                setSearchResults([]);
                setShowResults(false);
                return;
            }
            try {
                const response = await api.get(`v1/apps/${appId}/organizations/?search=${encodeURIComponent(value)}`);
                if (response.data) {
                    setSearchResults(response.data);
                    setShowResults(true);
                } else {
                    setStatus({ type: 'error', message: 'Erro ao buscar' });
                }
            } catch (error) {
                setStatus({ type: 'error', message: 'Erro ao buscar' });
                console.error('Erro na busca:', error);
            }
        }, 400),
        []
    );

    const handleChangeSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        handleSearch(value);
    };

    const handleSelectOrganization = (org) => {
        setFormData({
            ...formData,
            organization: org.id,
            organization_name: org.name
        });
        setSearchTerm(org.name);
        setShowResults(false);
    };

    const clearOrganization = () => {
        setFormData({
            ...formData,
            organization: null,
            organization_name: ''
        });
        setSearchTerm('');
        setShowResults(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: '' });

        // if ((formData.role === 'admin' || formData.role === 'agent') && !formData.password) {
        //     setStatus({ type: 'error', message: 'Senha é obrigatória para administradores e membros da empresa.' });
        //     return;
        // }

        // if (formData.role === 'customer') {
        //     console.log('Gerando senha automática para usuário final');
        //     setFormData({
        //         ...formData,
        //         password: '0' //Math.random().toString(36).slice(-8) // Gera uma senha aleatória de 8 caracteres
        //     });
        // }

        console.log(`POST user: ${JSON.stringify(formData)}`);

        try {
            const visibleNames = new Set(fieldsConfig.filter((f) => isFieldVisible(f, fieldsConfig, fields)).map((f) => f.name));
            const fieldsToSend = Object.fromEntries(Object.entries(fields).filter(([name]) => visibleNames.has(name)));

            const response = await api.post(`v1/apps/${appId}/users/`, { ...formData, fields: fieldsToSend });
            console.log('Resposta da API:', response);
            if (response.status === 201) {
                setStatus({ type: 'success', message: 'Usuário criado com sucesso' });
                backPage();
                return;
            }
            if (response.data) {
                setStatus({ type: 'success', message: 'Usuário criado com sucesso!' });

                setFormData({
                    email: '',
                    first_name: '',
                    last_name: '',
                    username: '',
                    role: '',
                    phone: '',
                    avatar: ''
                });

                setSearchTerm('');
                setShowResults(false);
                setFields({});
                // backPage;
            } else {
                setStatus({ type: 'error', message: 'Erro ao criar usuário' });
            }
        } catch (error) {
            setStatus({
                type: 'error',
                message: error.response?.data?.detail || 'Erro ao criar usuário.'
            });
        }
    };

    return (
        <Box>
            <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
                Criar Usuário
            </Typography>

            <Card>
                <CardContent>
                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            {/* Nomes */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="first_name"
                                    label="Primeiro Nome"
                                    fullWidth
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="last_name"
                                    label="Segundo Nome"
                                    fullWidth
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>

                            {/* Username gerado automaticamente */}
                            {/* <Grid item xs={12}></Grid> */}

                            {/* Email */}
                            <Grid item xs={12} sm={6}>
                                <TextField name="email" label="Email" fullWidth value={formData.email} onChange={handleChange} required />
                            </Grid>

                            {/* Role */}
                            <Grid item xs={12} sm={6}>
                                <TextField name="username" label="Username" fullWidth value={formData.username} disabled />
                                {/* <TextField
                                    name="role"
                                    select
                                    label="Nível de Permissão"
                                    fullWidth
                                    value={formData.role}
                                    onChange={handleChange}
                                >
                                    <MenuItem value="admin">Administrador</MenuItem>
                                    <MenuItem value="agent">Membro da Empresa</MenuItem>
                                    <MenuItem value="customer">Usuário Final</MenuItem>
                                </TextField> */}
                            </Grid>

                            {/* Campo de busca da Organização */}
                            <Grid item xs={12}>
                                <OutlinedInput
                                    fullWidth
                                    placeholder="Buscar organização por nome, CNPJ ou CPF..."
                                    value={searchTerm}
                                    onChange={handleChangeSearch}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <IconSearch size="1.2rem" color="#666" />
                                        </InputAdornment>
                                    }
                                />
                            </Grid>

                            {/* Lista de resultados */}
                            {showResults && (
                                <Grid item xs={12}>
                                    <Paper elevation={3}>
                                        <List>
                                            {searchResults.length === 0 && <ListItem>Nenhuma organização encontrada</ListItem>}

                                            {searchResults.map((org) => (
                                                <ListItem disablePadding key={org.id}>
                                                    <ListItemButton onClick={() => handleSelectOrganization(org)}>
                                                        {org.name}
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}

                                            <ListItem disablePadding>
                                                <ListItemButton onClick={clearOrganization}>Sem organização</ListItemButton>
                                            </ListItem>
                                        </List>
                                    </Paper>
                                </Grid>
                            )}

                            {/* Telefone */}
                            <Grid item xs={12} sm={6}>
                                <TextField name="phone" label="Telefone" fullWidth value={formData.phone} onChange={handleChange} />
                            </Grid>

                            {/* Avatar */}
                            {/* <Grid item xs={12} sm={6}>
                                <TextField name="avatar" label="Avatar (URL)" fullWidth value={formData.avatar} onChange={handleChange} />
                            </Grid> */}

                            {fieldsConfig
                                .filter((f) => isFieldVisible(f, fieldsConfig, fields))
                                .map((f) => (
                                    <CustomFieldInput
                                        key={f.id}
                                        field={f}
                                        value={fields[f.name]}
                                        onChange={(value) => setFields({ ...fields, [f.name]: value })}
                                    />
                                ))}

                            {(formData.role === 'admin' || formData.role === 'agent') && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="password"
                                        label="Senha"
                                        fullWidth
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        type="password"
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button
                                        onClick={backPage}
                                        sx={{
                                            borderRadius: '12px',
                                            px: 3,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
                                            color: '#fff',
                                            boxShadow: '0 8px 20px rgba(239,68,68,0.4)',
                                            transition: '0.3s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 12px 30px rgba(239,68,68,0.6)'
                                            }
                                        }}
                                    >
                                        Voltar
                                    </Button>

                                    {/* LIMPAR */}
                                    <Button
                                        type="reset"
                                        variant="outlined"
                                        onClick={() => {
                                            setFormData({
                                                email: '',
                                                first_name: '',
                                                last_name: '',
                                                username: '',
                                                role: '',
                                                phone: '',
                                                avatar: ''
                                            });
                                            setSearchTerm('');
                                            setShowResults(false);
                                            setFields({});
                                        }}
                                        sx={{
                                            borderRadius: '12px',
                                            px: 3,
                                            py: 1,
                                            color: '#ccc',
                                            borderColor: '#555',
                                            textTransform: 'none',
                                            '&:hover': {
                                                borderColor: '#999',
                                                background: 'rgba(255,255,255,0.05)'
                                            }
                                        }}
                                    >
                                        Limpar
                                    </Button>

                                    <Button
                                        type="submit"
                                        sx={{
                                            borderRadius: '12px',
                                            px: 4,
                                            py: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            background: 'linear-gradient(135deg, #22c55e, #4ade80)',
                                            color: '#000',
                                            boxShadow: '0 8px 20px rgba(34,197,94,0.4)',
                                            transition: '0.3s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 12px 30px rgba(34,197,94,0.6)'
                                            }
                                        }}
                                    >
                                        💾 Criar Usuário
                                    </Button>
                                </Box>
                            </Grid>

                            {/* Botões */}
                            {/* <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button
                                        onClick={backPage}
                                        variant="contained"
                                        sx={{
                                            backgroundColor: '#a84a32',
                                            textTransform: 'none'
                                        }}
                                    >
                                        Voltar
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => {
                                            setFormData({
                                                email: '',
                                                first_name: '',
                                                last_name: '',
                                                username: '',
                                                role: '',
                                                phone: '',
                                                avatar: ''
                                            });
                                            setSearchTerm('');
                                            setShowResults(false);
                                        }}
                                    >
                                        Limpar
                                    </Button>

                                    <Button type="submit" variant="contained" sx={{ backgroundColor: '#32CD32' }}>
                                        Criar Usuário
                                    </Button>
                                </Box>
                            </Grid> */}
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CreateUser;
